import { Link } from 'react-router-dom'
import { HardHat, CheckCircle, Clock, Award, Ruler, Building } from 'lucide-react'
import Hero from '../../components/Hero'
import SEO from '../../components/SEO'

export default function ConstructionManagement() {
  const requirements = [
    'High school diploma with mathematics and science',
    'Strong analytical and problem-solving skills',
    'Personal statement',
    'Two letters of recommendation',
    'Completed application form',
    'Interview with faculty'
  ]

  const courses = [
    'Construction Project Management',
    'Building Materials & Methods',
    'Construction Planning & Scheduling',
    'Cost Estimation & Budgeting',
    'Construction Safety & Health',
    'Contract Administration',
    'Quality Control & Assurance',
    'Construction Equipment Management',
    'Site Supervision & Coordination',
    'Building Codes & Regulations',
    'Leadership & Team Management',
    'Professional Ethics in Construction'
  ]

  const careers = [
    'Construction Manager',
    'Project Coordinator',
    'Site Supervisor',
    'Construction Estimator',
    'Project Scheduler',
    'Quality Control Manager',
    'Construction Safety Officer',
    'Contract Administrator'
  ]

  return (
    <>
      <SEO 
        title="Construction Management Programme - Rudovia Life College"
        description="Master the skills to plan, coordinate, and supervise construction projects. Learn resource management, budgeting, and professional standards in the construction industry."
        keywords="construction management Zimbabwe, construction course Africa, project management construction, building management training, construction education"
      />
      <div className="pt-20">
        <Hero
          title="Construction Management Programme"
          subtitle="Equipping leaders for excellence in the construction industry"
          image="/assets/images/pexels-pixabay-268362.jpg"
          height="h-[400px]"
          primaryButton={{ text: 'Apply Now', link: '/admissions' }}
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src="/assets/images/pexels-pixabay-268362.jpg" 
                  alt="Construction Management" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="heading-2 mb-6">Programme Overview</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  The Construction Management course exists to equip students with the practical skills, technical knowledge, and leadership ability needed to successfully plan, coordinate, and supervise construction projects from start to finish.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  It prepares learners to manage people, resources, timelines, and budgets effectively while maintaining quality, safety, and professional standards in the construction industry.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Through hands-on training and real-world project experience, students develop the competencies required to lead construction teams, coordinate complex projects, and deliver successful outcomes that meet client expectations and industry standards.
                </p>
                <div className="flex items-center space-x-8 mt-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Duration</div>
                      <div className="text-gray-600">2-3 Years</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-secondary" />
                    <div>
                      <div className="font-bold text-primary">Qualification</div>
                      <div className="text-gray-600">Diploma/Degree</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <Building className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Core Courses</h3>
                <ul className="space-y-2">
                  {courses.map((course, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{course}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <Ruler className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Admission Requirements</h3>
                <ul className="space-y-2">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <HardHat className="w-12 h-12 text-secondary mb-4" />
                <h3 className="text-2xl font-bold text-primary mb-4">Career Opportunities</h3>
                <ul className="space-y-2">
                  {careers.map((career, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{career}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-primary text-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Study Construction Management at Rudovia?</h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Practical Skills</h3>
                  <p className="text-gray-200">
                    Gain hands-on experience in project planning, site management, and construction coordination through real-world projects.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Industry Standards</h3>
                  <p className="text-gray-200">
                    Learn current industry practices, safety protocols, and quality standards that prepare you for professional success.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Leadership Development</h3>
                  <p className="text-gray-200">
                    Develop the leadership and management skills needed to coordinate teams and deliver successful construction projects.
                  </p>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold mb-3 text-secondary">Professional Ethics</h3>
                  <p className="text-gray-200">
                    Build a foundation of integrity, accountability, and ethical practice in all aspects of construction management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom text-center">
            <h2 className="heading-2 mb-6">Build Your Future in Construction Management</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Transform your passion for building into a rewarding career. Apply to our Construction Management programme today and develop the skills to lead successful construction projects.
            </p>
            <Link to="/admissions" className="btn-primary">
              Apply Now
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
